import React, { useEffect, useState } from 'react';
import { Alert, Col, DatePicker, Form, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';
import CBInputNumber from './CBInputNumber';
import axiosInstance from '../variables/api/axiosInstance';

const GREEN = '#1a6b3c';
const pasDansLeFutur = (d) => d && d > dayjs().endOf('day');

/**
 * Date de consolidation d'une victime blessée, saisie depuis la fiche du dossier.
 *
 * La consolidation se lisait sur une expertise médicale, qui ne s'enregistrait qu'après
 * avoir désigné un expert dans l'onglet Expertises. Quand le rapport arrive comme pièce
 * du dossier, rien ne permettait de la poser : la victime restait « non mûre » avec un
 * dossier complet. IPP et ITT se saisissent au même endroit : l'offre en a besoin.
 *
 * `personnes` : la maturité des personnes (GET /v1/sinistres/{id}/maturite), dont on
 * garde celles qui attendent une consolidation.
 */
export default function ConsolidationModal({ open, onClose, personnes = [], onEnregistre }) {
  const { t } = useTranslation('commun');
  const [form] = Form.useForm();
  const [envoi, setEnvoi] = useState(false);
  const victimeId = Form.useWatch('victimeTrackingId', form);
  const candidates = personnes.filter((p) => p.consolidationRequise);
  const enAttente = candidates.filter((p) => !p.estConsolidee);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const premiere = enAttente[0] || candidates[0];
    if (premiere) form.setFieldValue('victimeTrackingId', premiere.victimeTrackingId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ce que l'expertise la plus récente sait déjà : on complète, on ne ressaisit pas.
  useEffect(() => {
    if (!open || !victimeId) return;
    let ignore = false;
    axiosInstance.get(`/v1/expertises-medicales/victime/${victimeId}`, { skipErrorToast: true })
      .then((r) => {
        if (ignore) return;
        const e = (r.data?.data || [])[0];
        form.setFieldsValue({
          dateConsolidation: e?.dateConsolidation ? dayjs(e.dateConsolidation) : undefined,
          dateRapport: e?.dateRapport ? dayjs(e.dateRapport) : undefined,
          tauxIpp: e?.tauxIpp != null && Number(e.tauxIpp) > 0 ? Number(e.tauxIpp) : undefined,
          dureeIttJours: e?.dureeIttJours ? e.dureeIttJours : undefined,
        });
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, [open, victimeId, form]);

  const valider = async () => {
    const v = await form.validateFields();
    setEnvoi(true);
    try {
      await axiosInstance.put(`/v1/expertises-medicales/victime/${v.victimeTrackingId}/consolidation`, {
        dateConsolidation: v.dateConsolidation.format('YYYY-MM-DD'),
        dateRapport: v.dateRapport ? v.dateRapport.format('YYYY-MM-DD') : null,
        tauxIpp: v.tauxIpp ?? null,
        dureeIttJours: v.dureeIttJours ?? null,
      });
      toast.success(t('consolidationModal.enregistree'));
      onEnregistre?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('consolidationModal.echecEnregistrement'));
    } finally {
      setEnvoi(false);
    }
  };

  const nom = (p) => {
    const base = p.victimeNomComplet || t('consolidationModal.victime');
    const avecRole = p.estAdversaire ? t('consolidationModal.nomPartieAdverse', { nom: base }) : base;
    return p.estConsolidee ? t('consolidationModal.nomDejaConsolidee', { nom: avecRole }) : avecRole;
  };

  return (
    <CBModal
      title={t('consolidationModal.titre')}
      open={open}
      onCancel={onClose}
      onOk={valider}
      okText={t('consolidationModal.enregistrer')}
      cancelText={t('consolidationModal.annuler')}
      confirmLoading={envoi}
      width={560}
      okButtonProps={{ disabled: candidates.length === 0, style: { background: GREEN, borderColor: GREEN } }}
      destroyOnClose
    >
      {candidates.length === 0 ? (
        <Alert type="info" showIcon message={t('consolidationModal.aucuneVictime')} />
      ) : (
        <Form form={form} layout="vertical" requiredMark={false}>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
            {t('consolidationModal.explication')}
          </div>
          <Form.Item name="victimeTrackingId" label={t('consolidationModal.victime')} rules={[{ required: true, message: t('consolidationModal.choisissezVictime') }]}>
            <Select options={candidates.map((p) => ({ value: p.victimeTrackingId, label: nom(p) }))} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="dateConsolidation" label={t('consolidationModal.titre')}
                rules={[{ required: true, message: t('consolidationModal.dateRequise') }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={pasDansLeFutur} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="dateRapport" label={t('consolidationModal.dateRapport')}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={pasDansLeFutur} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="tauxIpp" label={t('consolidationModal.tauxIpp')} extra={t('consolidationModal.utileOffre')}>
                <CBInputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="dureeIttJours" label={t('consolidationModal.itt')}>
                <CBInputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </CBModal>
  );
}
